import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    FlaskConical, Trash2, Barcode, Plus, Save,
    ArrowRightCircle, AlertTriangle, CalendarDays, User2, Ban, Truck, ScanLine, Printer
} from 'lucide-react';
import { toast } from 'react-toastify';
import LabServices from '../../shared/ApiServices/LabServices';
import { SampleListSkeleton } from '../../shared/custom-ui/skeleton/Lab';
import { Button, Input, TextArea, Dialog, Select } from '../../shared/custom-ui';
import useLabPermissions from './useLabPermissions';
import {
    extractLabSamplesList, extractLabSampleId,
    normalizeSampleStatus, SAMPLE_STATUS_META, type SampleStatus,
    extractSampleTypesList,
    extractLabTestsList, extractLabTestId, extractTestName,
    formatLabDate,
    type LabSample, type LabSampleType, type LabTest, type LabOrder,
} from './labUtils';

// Specimen Transport + Laboratory Accessioning pipeline
const NEXT_SAMPLE_STATUS: Record<SampleStatus, SampleStatus | null> = {
    pending: 'collected', collected: 'in_transit', in_transit: 'accessioned', accessioned: null, rejected: null,
};
const ADVANCE_LABEL: Record<SampleStatus, string> = {
    pending: 'Mark Collected', collected: 'Send for Transit', in_transit: 'Accession Sample', accessioned: '', rejected: '',
};
const ADVANCE_ICON: Record<SampleStatus, React.ElementType> = {
    pending: ArrowRightCircle, collected: Truck, in_transit: ScanLine, accessioned: ArrowRightCircle, rejected: ArrowRightCircle,
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[12px] font-semibold text-[var(--v2-text-secondary)]">
            {label}{required && <span className="text-[var(--v2-danger)] ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

// ─── Delete Confirmation Modal ─────────────────────────────────────────────────
const DeleteConfirmModal: React.FC<{
    sampleName: string;
    barcodeNumber?: string;
    onConfirm: () => void;
    onCancel: () => void;
    deleting: boolean;
}> = ({ sampleName, barcodeNumber, onConfirm, onCancel, deleting }) => (
    <Dialog isOpen onClose={onCancel} size="small" preventCloseOnOverlayClick showCloseButton={false}>
        <div className="flex flex-col gap-4 -mt-2">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.1)' }}>
                    <AlertTriangle className="w-5 h-5 text-red-600" strokeWidth={2.5} />
                </div>
                <div>
                    <h3 className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0">Delete Sample?</h3>
                    <p className="text-[12px] font-medium text-[var(--v2-text-secondary)] m-0">This action cannot be undone.</p>
                </div>
            </div>
            <div className="rounded-lg px-3.5 py-2.5 flex flex-col gap-0.5 border" style={{ background: 'rgba(239,68,68,0.05)', borderColor: 'rgba(239,68,68,0.2)' }}>
                <p className="text-[13px] font-bold text-[var(--v2-text-primary)] m-0">{sampleName}</p>
                {barcodeNumber && <p className="text-[11px] font-mono text-red-600 font-semibold m-0 tracking-wide">Barcode #{barcodeNumber}</p>}
            </div>
            <div className="flex gap-3">
                <Button variant="outline" fullWidth onClick={onCancel} disabled={deleting}>Keep Sample</Button>
                <Button variant="danger" fullWidth onClick={onConfirm} loading={deleting}>Yes, Delete</Button>
            </div>
        </div>
    </Dialog>
);

// ─── Reject Sample Modal ────────────────────────────────────────────────────────
const RejectSampleModal: React.FC<{ onConfirm: (reason: string) => void; onClose: () => void; rejecting: boolean }> = ({ onConfirm, onClose, rejecting }) => {
    const [reason, setReason] = useState('');
    return (
        <Dialog isOpen onClose={onClose} size="small" preventCloseOnOverlayClick showCloseButton={false}>
            <div className="flex flex-col gap-4 -mt-2">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-red-100">
                        <Ban className="w-5 h-5 text-red-600" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h3 className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0">Reject Sample</h3>
                        <p className="text-[12px] font-medium text-[var(--v2-text-secondary)] m-0 leading-relaxed">Record why this specimen failed integrity checks.</p>
                    </div>
                </div>
                <TextArea label="Reason for Rejection" required rows={3} placeholder="e.g. Hemolysed sample, insufficient volume" value={reason} onChange={e => setReason(e.target.value)} />
                <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="danger" onClick={() => onConfirm(reason)} disabled={!reason.trim()} loading={rejecting} leftIcon={<Ban className="w-full h-full" strokeWidth={2} />}>
                        Reject Sample
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

// ─── Barcode Modal ─────────────────────────────────────────────────────────────
const BarcodeModal: React.FC<{ sampleId: string; onClose: () => void }> = ({ sampleId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [svgHtml, setSvgHtml] = useState('');
    const [error, setError] = useState('');
    const printRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await LabServices.getSampleBarcode(sampleId);
                const b64 = res?.barcodeSvgBase64 ?? res?.barcode_svg_base64 ?? res?.svg ?? res?.barcodeData ?? res?.barcode;
                if (b64) {
                    setSvgHtml(atob(b64));
                } else if (typeof res === 'string' && res.includes('<svg')) {
                    setSvgHtml(res);
                } else {
                    setError('Barcode data not found in response');
                }
            } catch (err: any) {
                setError(err?.message ?? 'Failed to load barcode');
            } finally {
                setLoading(false);
            }
        })();
    }, [sampleId]);

    const handlePrint = () => {
        if (!printRef.current) return;
        const win = window.open('', '_blank', 'width=400,height=300');
        if (!win) { toast.error('Please allow popups to print'); return; }
        win.document.write(`<html><head><title>Barcode</title><style>body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh;}</style></head><body>${printRef.current.innerHTML}</body></html>`);
        win.document.close();
        win.onload = () => { win.print(); win.close(); };
    };

    return (
        <Dialog isOpen onClose={onClose} title="Specimen Barcode" size="small">
            {loading ? (
                <div className="h-40 rounded-xl bg-[var(--v2-bg-surface)] border border-[var(--v2-border-card)] animate-pulse" />
            ) : error ? (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex flex-col gap-2 items-center text-center">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                    <p className="text-[13px] font-bold text-red-700 m-0">{error}</p>
                </div>
            ) : (
                <div className="flex flex-col gap-4 items-center">
                    <div className="w-full bg-white rounded-xl border border-[var(--v2-border-card)] p-6 flex flex-col items-center gap-3">
                        <div ref={printRef} className="w-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-auto [&>svg]:max-w-[250px]"
                            dangerouslySetInnerHTML={{ __html: svgHtml }} />
                    </div>
                    <Button variant="primary" fullWidth onClick={handlePrint} leftIcon={<Printer className="w-full h-full" strokeWidth={2} />}>
                        Print Barcode Label
                    </Button>
                </div>
            )}
        </Dialog>
    );
};

// ─── Register Sample Modal ─────────────────────────────────────────────────────
const RegisterSampleModal: React.FC<{
    orderId: string;
    tests: LabTest[];
    sampleTypes: LabSampleType[];
    onClose: () => void;
    onSuccess: () => void;
}> = ({ orderId, tests, sampleTypes, onClose, onSuccess }) => {
    const [form, setForm] = useState({
        testId: '', sampleTypeName: '', bottleType: '', collectedMaxQty: '', collectedMinQty: '', collectedBy: '',
    });
    const [saving, setSaving] = useState(false);

    const handleSampleTypeChange = (typeName: string) => {
        const matched = sampleTypes.find(st => (st.sampleTypeName ?? (st as any).sample_type_name) === typeName);
        setForm(f => ({
            ...f,
            sampleTypeName: typeName,
            bottleType: matched ? ((matched as any).bottleType ?? (matched as any).bottle_type ?? f.bottleType) : f.bottleType,
        }));
    };

    const handleSave = async () => {
        if (!form.testId) { toast.error('Select a test'); return; }
        setSaving(true);
        try {
            const res: any = await LabServices.registerSample(orderId, { ...form, testId: form.testId });
            const barcode = res?.barcodeNumber ?? res?.barcode_number;
            toast.success(barcode
                ? `${res?.message ?? 'Sample registered'} — Barcode: ${barcode}`
                : (res?.message ?? 'Sample registered')
            );
            onSuccess(); onClose();
        } catch (err: any) { toast.error(err?.message ?? 'Failed'); } finally { setSaving(false); }
    };

    return (
        <Dialog isOpen onClose={onClose} title="Register Specimen Vial" size="medium" preventCloseOnOverlayClick>
            <div className="flex flex-col gap-5 -mt-1">
                <div className="flex items-center gap-3 p-3 rounded-xl border" style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.05))', borderColor: 'var(--v2-primary-400, rgba(4,94,184,0.25))' }}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-white border border-[var(--v2-border-card)]">
                        <FlaskConical className="w-5 h-5" style={{ color: 'var(--v2-primary-500)' }} strokeWidth={1.5} />
                    </div>
                    <div>
                        <p className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">New Specimen</p>
                        <p className="text-[12px] font-medium text-[var(--v2-text-muted)] m-0">Register a new vial for this order.</p>
                    </div>
                </div>

                <div className="flex flex-col gap-3.5">
                    <Field label="Test" required>
                        <Select
                            value={form.testId}
                            onChange={v => setForm(f => ({ ...f, testId: v }))}
                            placeholder="— Select test —"
                            options={tests.map(t => ({ value: extractLabTestId(t), label: extractTestName(t) }))}
                        />
                    </Field>

                    <Field label="Sample Type">
                        <Select
                            value={form.sampleTypeName}
                            onChange={handleSampleTypeChange}
                            placeholder="— Select type —"
                            options={sampleTypes.map(st => ({
                                value: st.sampleTypeName ?? (st as any).sample_type_name ?? '',
                                label: st.sampleTypeName ?? (st as any).sample_type_name ?? '',
                            }))}
                        />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <Input label="Bottle Type" value={form.bottleType} onChange={e => setForm(f => ({ ...f, bottleType: e.target.value }))} placeholder="e.g. Lavender EDTA Tube" />
                        <Input label="Collected By" value={form.collectedBy} onChange={e => setForm(f => ({ ...f, collectedBy: e.target.value }))} placeholder="Technician name" />
                    </div>

                    <div className="grid grid-cols-2 gap-3.5">
                        <Input label="Min Qty" value={form.collectedMinQty} onChange={e => setForm(f => ({ ...f, collectedMinQty: e.target.value }))} placeholder="e.g. 3 mL" />
                        <Input label="Max Qty" value={form.collectedMaxQty} onChange={e => setForm(f => ({ ...f, collectedMaxQty: e.target.value }))} placeholder="e.g. 5 mL" />
                    </div>
                </div>

                <div className="flex gap-3 justify-end pt-3 border-t border-[var(--v2-border-subtle)]">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} loading={saving} leftIcon={!saving ? <Save className="w-full h-full" strokeWidth={2} /> : undefined}>
                        Register Vial
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

interface SampleCollectionProps {
    orderId?: string;
    onSampleChange?: () => void;
    order?: LabOrder;
    labTests?: LabTest[];
}
export const SampleCollection: React.FC<SampleCollectionProps> = ({ orderId: orderIdProp, onSampleChange, order: orderProp, labTests: labTestsProp }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const orderId = orderIdProp ?? searchParams.get('orderId') ?? '';
    const { canCollectSamples, currentUser } = useLabPermissions();

    const [samples, setSamples] = useState<LabSample[]>([]);
    const [orderTests, setOrderTests] = useState<{ testId: string; testName: string }[]>([]);
    const [slideLibraryStatus, setSlideLibraryStatus] = useState<string | null>(null);
    const [sampleTypes, setSampleTypes] = useState<LabSampleType[]>([]);
    const [loading, setLoading] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [barcodeTarget, setBarcodeTarget] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<LabSample | null>(null);
    const [toggling, setToggling] = useState<string | null>(null);
    const [rejectTarget, setRejectTarget] = useState<string | null>(null);
    const [rejecting, setRejecting] = useState(false);
    const mountedRef = useRef(true);

    const loadSamples = useCallback(async () => {
        if (!orderId) return;
        setLoading(true);
        try {
            const [s, st, t, orderRes] = await Promise.all([
                LabServices.getSamplesForOrder(orderId),
                LabServices.listSampleTypes(),
                labTestsProp ? Promise.resolve(null) : LabServices.getLabTests(),
                orderProp ? Promise.resolve(null) : LabServices.getOrder(orderId).catch(() => null),
            ]);
            if (mountedRef.current) {
                setSamples(extractLabSamplesList(s));
                setSampleTypes(extractSampleTypesList(st));
                const oData: any = orderProp ?? ((orderRes as any)?.order ?? orderRes);

                const catalogById = new Map((labTestsProp ?? extractLabTestsList(t)).map(ct => [extractLabTestId(ct), ct]));
                const topLevelTests: any[] = orderProp ? [] : ((orderRes as any)?.tests ?? []);
                const orderTestsRaw: any[] = oData?.tests ?? [];
                const nameFor = (tid: string, fallback?: string) =>
                    fallback || (catalogById.has(tid) ? extractTestName(catalogById.get(tid)!) : tid);

                const seenIds = new Set<string>();
                const oTests: { testId: string; testName: string }[] = [
                    ...orderTestsRaw.map((ot: any) => {
                        const tid = ot.testId ?? ot.test_id ?? '';
                        seenIds.add(tid);
                        return { testId: tid, testName: nameFor(tid, ot.testName ?? ot.test_name) };
                    }),
                    ...topLevelTests
                        .map((tt: any) => tt.testId ?? tt.test_id ?? String(tt.SK ?? '').replace(/^TEST#/, ''))
                        .filter((tid: string) => tid && !seenIds.has(tid))
                        .map((tid: string) => { seenIds.add(tid); return { testId: tid, testName: nameFor(tid) }; }),
                ].filter(ot => ot.testId);

                setOrderTests(oTests);
                setSlideLibraryStatus(oData?.slideLibraryStatus ?? oData?.slide_library_status ?? null);
            }
        } catch (err: any) { toast.error(err?.message ?? 'Failed to load samples'); }
        finally { if (mountedRef.current) setLoading(false); }
    }, [orderId, orderProp, labTestsProp]);

    useEffect(() => { mountedRef.current = true; loadSamples(); return () => { mountedRef.current = false; }; }, [loadSamples]);

    const advanceStatus = async (sample: LabSample) => {
        const current = normalizeSampleStatus(sample);
        const next = NEXT_SAMPLE_STATUS[current];
        if (!next) return;
        const sId = extractLabSampleId(sample);
        setToggling(sId);
        try {
            const stamp: Record<string, any> = { status: next };
            if (next === 'collected') stamp.isCollected = true;
            if (next === 'in_transit') stamp.inTransitAt = new Date().toISOString();
            if (next === 'accessioned') {
                stamp.accessionedBy = currentUser?.name ?? currentUser?.id ?? 'Unknown user';
                stamp.accessionedAt = new Date().toISOString();
            }
            const res: any = await LabServices.updateSample(sId, stamp);
            setSamples(prev => prev.map(s => extractLabSampleId(s) === sId ? { ...s, ...stamp } : s));
            toast.success(res?.message ?? `Marked as ${SAMPLE_STATUS_META[next].label}`);
            onSampleChange?.();
        } catch (err: any) { toast.error(err?.message ?? 'Update failed'); }
        finally { setToggling(null); }
    };

    const confirmReject = async (reason: string) => {
        if (!rejectTarget) return;
        setRejecting(true);
        try {
            const stamp = { isRejected: true, rejectionReason: reason, status: 'rejected' as const };
            const res: any = await LabServices.updateSample(rejectTarget, stamp);
            setSamples(prev => prev.map(s => extractLabSampleId(s) === rejectTarget ? { ...s, ...stamp } : s));
            toast.success(res?.message ?? 'Sample rejected');
            setRejectTarget(null);
            onSampleChange?.();
        } catch (err: any) { toast.error(err?.message ?? 'Reject failed'); }
        finally { setRejecting(false); }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const sId = extractLabSampleId(deleteTarget);
        setDeleting(sId);
        try {
            const res: any = await LabServices.deleteSample(sId);
            setSamples(prev => prev.filter(s => extractLabSampleId(s) !== sId));
            toast.success(res?.message ?? 'Sample deleted');
            setDeleteTarget(null);
            onSampleChange?.();
        } catch (err: any) { toast.error(err?.message ?? 'Delete failed'); }
        finally { setDeleting(null); }
    };

    if (!orderId) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.08))' }}>
                    <FlaskConical className="w-8 h-8" style={{ color: 'var(--v2-primary-500)' }} strokeWidth={1.5} />
                </div>
                <div className="flex flex-col gap-1.5">
                    <p className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0">Select an Order First</p>
                    <p className="text-[13px] text-[var(--v2-text-muted)] max-w-sm m-0 leading-relaxed">
                        Sample collection is linked to a specific order. Open an order and click <strong className="text-[var(--v2-text-primary)]">"Register Samples"</strong> to get started.
                    </p>
                </div>
                <Button variant="primary" onClick={() => navigate('/lab/orders')} leftIcon={<FlaskConical className="w-full h-full" strokeWidth={2} />}>
                    Go to Orders
                </Button>
            </div>
        );
    }

    if (loading) return <SampleListSkeleton />;

    return (
        <div className="flex flex-col lg:flex-row gap-4 h-full">
            {/* ── Left panel ── */}
            <div className="w-full lg:w-[260px] flex-shrink-0 flex flex-col gap-4">
                <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col h-full">
                    <div className="px-4 py-3 flex flex-col gap-3 bg-[var(--v2-bg-surface)] border-b border-[var(--v2-border-subtle)]">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--v2-text-muted)] m-0 mb-1">Order Details</p>
                            <p className="text-[12px] font-mono font-bold text-[var(--v2-text-primary)] m-0 truncate px-2.5 py-1 rounded-md bg-[var(--v2-bg-card)] border border-[var(--v2-border-subtle)] inline-block">
                                #{orderId.slice(-12)}
                            </p>
                        </div>
                    </div>
                    <div className="p-4 flex flex-col gap-2.5 flex-1 overflow-y-auto v2-scrollbar">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-[var(--v2-text-muted)] m-0 flex items-center gap-1.5">
                            <FlaskConical className="w-3 h-3" /> Tests in Order
                        </p>
                        {orderTests.length === 0 ? (
                            <p className="text-[12px] font-medium text-[var(--v2-text-muted)] italic m-0">No tests linked</p>
                        ) : (
                            <div className="flex flex-col gap-1.5">
                                {orderTests.map(t => (
                                    <div key={t.testId} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[var(--v2-bg-surface)] transition-colors">
                                        <div className="w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center bg-[var(--v2-bg-surface)] border border-[var(--v2-border-card)]">
                                            <FlaskConical className="w-3 h-3" style={{ color: 'var(--v2-primary-500)' }} strokeWidth={2} />
                                        </div>
                                        <span className="text-[12px] text-[var(--v2-text-primary)] font-semibold truncate">{t.testName}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Right panel ── */}
            <div className="flex-1 rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col h-full">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)]">
                    <div className="flex items-center gap-2">
                        <h3 className="text-[12px] font-bold uppercase tracking-wide text-[var(--v2-text-heading)] m-0">Specimens</h3>
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold" style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.08))', color: 'var(--v2-primary-600, #045eb8)' }}>
                            {samples.length} Total
                        </span>
                    </div>
                    {canCollectSamples && (
                        <Button variant="primary" size="small" onClick={() => setShowRegister(true)} leftIcon={<Plus className="w-full h-full" strokeWidth={2.5} />}>
                            Register Vial
                        </Button>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto v2-scrollbar p-0 bg-[var(--v2-bg-base)]">
                    {samples.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center px-4 py-16">
                            <div className="w-16 h-16 rounded-full bg-[var(--v2-bg-surface)] flex items-center justify-center mb-4 border border-[var(--v2-border-card)]">
                                <FlaskConical className="w-8 h-8 text-[var(--v2-text-muted)] opacity-50" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0 mb-2">No Specimens Registered</h3>
                            <p className="text-[13px] font-medium text-[var(--v2-text-secondary)] m-0 max-w-sm leading-relaxed mb-5">
                                Register the first specimen vial to begin the sample collection and tracking process.
                            </p>
                            {canCollectSamples && (
                                <Button variant="primary" onClick={() => setShowRegister(true)}>Register First Vial</Button>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col p-2.5 gap-2.5">
                            {samples.map(sample => {
                                const sId = extractLabSampleId(sample);
                                let status = normalizeSampleStatus(sample);

                                const hasCollectedSlide = !!slideLibraryStatus && slideLibraryStatus !== 'PENDING' && slideLibraryStatus !== 'WAITING_FOR_LAB_REPORT';
                                if (status === 'pending' && hasCollectedSlide) {
                                    status = 'collected';
                                }

                                const statusMeta = SAMPLE_STATUS_META[status];
                                const rejected = status === 'rejected';
                                const nextStatus = NEXT_SAMPLE_STATUS[status];
                                const AdvanceIcon = ADVANCE_ICON[status];
                                const barcodeNum = sample.barcodeNumber ?? (sample as any).barcode_number;
                                const collectedDate = sample.collectedDate ?? (sample as any).collected_date ?? sample.createdAt ?? (sample as any).created_at;

                                return (
                                    <div key={sId} className="rounded-lg border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col">
                                        <div className="px-3.5 py-2.5 flex items-center justify-between gap-3">

                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border"
                                                    style={{ background: statusMeta.bg, borderColor: `${statusMeta.color}30` }}>
                                                    <FlaskConical className="w-4 h-4" strokeWidth={1.5} style={{ color: statusMeta.color }} />
                                                </div>
                                                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <h4 className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">
                                                            {sample.sampleTypeName ?? (sample as any).sample_type_name ?? 'Sample'}
                                                        </h4>
                                                        <span className="flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide"
                                                            style={{ background: statusMeta.color, color: 'white' }}>
                                                            {statusMeta.label}
                                                        </span>
                                                        {barcodeNum && (
                                                            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--v2-bg-surface)] border border-[var(--v2-border-subtle)]">
                                                                <Barcode className="w-3 h-3 text-[var(--v2-text-muted)]" />
                                                                <span className="font-mono font-bold text-[10px] text-[var(--v2-text-primary)]">#{barcodeNum}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <p className="text-[12px] font-medium text-[var(--v2-text-secondary)] m-0">
                                                        {[
                                                            sample.bottleType ?? (sample as any).bottle_type,
                                                            (sample.collectedMaxQty ?? (sample as any).collected_max_qty) &&
                                                            `${sample.collectedMinQty ?? (sample as any).collected_min_qty}–${sample.collectedMaxQty ?? (sample as any).collected_max_qty}`,
                                                        ].filter(Boolean).join(' · ')}
                                                    </p>
                                                    {rejected && sample.rejectionReason && (
                                                        <div className="mt-1 flex items-start gap-1.5 px-2.5 py-1.5 rounded-md bg-red-50 border border-red-100">
                                                            <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                                                            <p className="text-[11px] font-bold text-red-700 m-0">Rejected: {sample.rejectionReason}</p>
                                                        </div>
                                                    )}
                                                    {status === 'accessioned' && sample.accessionedBy && (
                                                        <p className="text-[11px] font-bold m-0 mt-1" style={{ color: '#059669' }}>
                                                            Accessioned by {sample.accessionedBy}{sample.accessionedAt ? ` · ${formatLabDate(sample.accessionedAt)}` : ''}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {canCollectSamples && nextStatus && (
                                                    <Button variant="outline" size="small" onClick={() => advanceStatus(sample)} loading={toggling === sId}
                                                        leftIcon={toggling !== sId ? <AdvanceIcon className="w-full h-full" strokeWidth={2} /> : undefined}
                                                        className="!text-[var(--v2-primary-600,#045eb8)] !border-[var(--v2-primary-400,rgba(4,94,184,0.35))]">
                                                        {ADVANCE_LABEL[status]}
                                                    </Button>
                                                )}
                                                <Button variant="outline" size="small" onClick={() => setBarcodeTarget(sId)}
                                                    leftIcon={<Barcode className="w-full h-full text-[var(--v2-text-muted)]" strokeWidth={2} />}>
                                                    Barcode
                                                </Button>
                                                {canCollectSamples && !rejected && (
                                                    <Button variant="outline" size="small" onClick={() => setRejectTarget(sId)}
                                                        leftIcon={<Ban className="w-full h-full text-[var(--v2-text-muted)]" strokeWidth={2} />}
                                                        className="hover:!text-red-600 hover:!bg-red-50 hover:!border-red-200">
                                                        Reject
                                                    </Button>
                                                )}
                                                {canCollectSamples && (
                                                    <button type="button" onClick={() => setDeleteTarget(sample)} disabled={deleting === sId}
                                                        className="w-7 h-7 ml-1 rounded-lg flex items-center justify-center cursor-pointer text-red-500 hover:text-white hover:bg-red-500 bg-red-50 border-0 disabled:opacity-50 transition-colors"
                                                        title="Delete sample">
                                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="px-3.5 py-1.5 bg-[var(--v2-bg-surface)] border-t border-[var(--v2-border-subtle)] flex items-center gap-3 flex-wrap">
                                            {(sample.collectedBy ?? (sample as any).collected_by) && (
                                                <span className="flex items-center gap-1 text-[10px] font-semibold text-[var(--v2-text-secondary)]">
                                                    <User2 className="w-3 h-3 text-[var(--v2-text-muted)]" strokeWidth={2} />
                                                    {sample.collectedBy ?? (sample as any).collected_by}
                                                </span>
                                            )}
                                            {collectedDate && (
                                                <span className="flex items-center gap-1 text-[10px] font-semibold text-[var(--v2-text-secondary)]">
                                                    <CalendarDays className="w-3 h-3 text-[var(--v2-text-muted)]" strokeWidth={2} />
                                                    {formatLabDate(collectedDate)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {showRegister && (
                <RegisterSampleModal orderId={orderId} tests={orderTests} sampleTypes={sampleTypes}
                    onClose={() => setShowRegister(false)} onSuccess={() => { loadSamples(); onSampleChange?.(); }} />
            )}
            {barcodeTarget && <BarcodeModal sampleId={barcodeTarget} onClose={() => setBarcodeTarget(null)} />}
            {rejectTarget && (
                <RejectSampleModal rejecting={rejecting} onConfirm={confirmReject} onClose={() => setRejectTarget(null)} />
            )}
            {deleteTarget && (
                <DeleteConfirmModal
                    sampleName={deleteTarget.sampleTypeName ?? (deleteTarget as any).sample_type_name ?? 'Sample'}
                    barcodeNumber={deleteTarget.barcodeNumber ?? (deleteTarget as any).barcode_number}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteTarget(null)}
                    deleting={deleting === extractLabSampleId(deleteTarget)}
                />
            )}
        </div>
    );
};

export default SampleCollection;
